/**
 * The server-side log window for one request (ADR 0025).
 *
 * A support reply is usually blocked on one question: what did the server do
 * in the seconds around this report? Today that is answered by somebody with
 * portal access opening a second tool, guessing a time range, and pasting what
 * they find. This module is the contract for answering it in place.
 *
 * It is deliberately built on `Provenance<T>` rather than on a fourth
 * discriminated union of its own. The failure this feature can most easily
 * ship is the one `provenance.ts` exists to prevent: a query that could not
 * run rendering as "no log lines", which reads as "the server did nothing
 * wrong" and closes the ticket. `Provenance` makes that impossible by
 * construction, because `unavailable` carries no value to mistake for an
 * empty one.
 *
 * The three states a reader must be able to tell apart are therefore:
 *
 *   known, lines non-empty   the window was searched and here is what it held
 *   known, lines empty       the window was searched and it was genuinely quiet
 *   unavailable              we could not find out, and this is why
 *
 * `logWindowState()` names those three so a screen switches on a value rather
 * than writing `lines.length === 0` at each call site; the second and third
 * are the pair that must never render alike.
 */

import { unavailable, type Provenance, type Unavailable } from './provenance.js';

/** One line as the backend returned it, normalised across query shapes. */
export interface LogLine {
  /** ISO 8601, from the log record itself and never from the query clock. */
  readonly timestamp: string;
  /**
   * Severity as the emitting system labelled it, lower-cased but otherwise
   * untranslated. Deliberately not an enum: a backend that invents `notice`
   * should show `notice`, not be flattened into `info` by this layer and
   * leave a reader wondering why the portal disagrees with the log tool.
   */
  readonly level: string;
  readonly message: string;
}

/**
 * What a successful search found, and the range it actually covered.
 *
 * The range is part of the answer rather than a detail of the query. "No lines"
 * is only meaningful alongside "between these two instants" — a quiet window
 * and a window that was one second wide render the same without it.
 */
export interface LogWindowContents {
  /** ISO 8601, inclusive. */
  readonly from: string;
  /** ISO 8601, inclusive. */
  readonly to: string;
  /** Oldest first. Empty means the window was searched and held nothing. */
  readonly lines: readonly LogLine[];
  /**
   * Whether the backend stopped returning rows before the window ended. A
   * truncated window that renders as complete is a quiet lie of the same
   * family as the one above, so it travels with the payload.
   */
  readonly truncated: boolean;
}

export type LogWindow = Provenance<LogWindowContents>;

/**
 * Why a window could not be determined, in the reader's terms.
 *
 * Each value answers "who fixes this", which is the only question the reason
 * is on screen to settle:
 *
 *   `not-configured`  an admin, in this portal: no log source for the app
 *   `no-credential`   whoever deploys: the environment carries no credential
 *   `no-trace-id`     nobody: the request predates trace capture
 *   `query-failed`    whoever runs the log backend: it was asked and did not answer
 */
export type LogWindowFailure =
  | 'not-configured'
  | 'no-credential'
  | 'no-trace-id'
  | 'query-failed';

/**
 * The sentence each failure puts on screen.
 *
 * Every one of them says what was not established, never what does not exist.
 * "No logs found" is banned wording here and is the whole point of the module.
 */
export const LOG_WINDOW_FAILURE_MESSAGES: Record<LogWindowFailure, string> = {
  'not-configured': 'No log source is configured for this application.',
  'no-credential': 'This deployment has no log credential, so the window could not be read.',
  'no-trace-id': 'This request was captured before trace ids were recorded, so its lines cannot be located.',
  'query-failed': 'The log backend did not answer, so the window could not be read.',
};

/** Build the `unavailable` half with a reason drawn from the closed set. */
export function logWindowUnavailable(failure: LogWindowFailure, source: string): Unavailable {
  return unavailable({ reason: LOG_WINDOW_FAILURE_MESSAGES[failure], source });
}

/**
 * The three states, named.
 *
 * `quiet` and `unknown` are the pair a screen must never collapse: one says
 * the server was silent, the other says we do not know whether it was.
 */
export type LogWindowState = 'lines' | 'quiet' | 'unknown';

export function logWindowState(window: LogWindow): LogWindowState {
  if (window.status === 'unavailable') return 'unknown';
  return window.value.lines.length > 0 ? 'lines' : 'quiet';
}
